import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Headphones, UserPlus, KeyRound, Power, PowerOff, Copy, Check } from 'lucide-react';

export default function AdminCallCenter() {
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ full_name: '', email: '', password: '' });
    const [busy, setBusy] = useState(false);
    const [lastCreated, setLastCreated] = useState(null);
    const [resetFor, setResetFor] = useState(null);
    const [newPw, setNewPw] = useState('');
    const [copied, setCopied] = useState(false);

    const load = () => api.get('/admin/callcenter/agents').then((r) => setAgents(r.data || [])).finally(() => setLoading(false));
    useEffect(() => { load(); }, []);

    const create = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const r = await api.post('/admin/callcenter/agents', form);
            toast.success(`Call Centre login created for ${r.data.email}`);
            setLastCreated({ email: form.email, password: form.password });
            setForm({ full_name: '', email: '', password: '' });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not create login');
        } finally { setBusy(false); }
    };

    const toggleActive = async (a) => {
        try {
            await api.patch(`/admin/callcenter/agents/${a.id}`, { is_active: !a.is_active });
            toast.success(`${a.email} ${a.is_active ? 'deactivated' : 'reactivated'}`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
    };

    const submitReset = async () => {
        if (newPw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
        try {
            await api.patch(`/admin/callcenter/agents/${resetFor.id}`, { password: newPw });
            toast.success(`Password reset for ${resetFor.email}`);
            setLastCreated({ email: resetFor.email, password: newPw });
            setResetFor(null); setNewPw('');
        } catch (e) { toast.error(e?.response?.data?.detail || 'Reset failed'); }
    };

    const copyCreds = () => {
        if (!lastCreated) return;
        navigator.clipboard.writeText(`Email: ${lastCreated.email}\nPassword: ${lastCreated.password}`);
        setCopied(true); setTimeout(() => setCopied(false), 1500);
    };

    return (
        <div className="max-w-5xl space-y-6" data-testid="admin-callcenter-page">
            <div>
                <h1 className="font-display font-extrabold text-2xl lg:text-3xl text-slate-900 flex items-center gap-2">
                    <Headphones className="h-6 w-6 text-brand-yellow" /> Call Centre Logins
                </h1>
                <p className="text-slate-500 mt-1 text-sm">Create view-only logins for the call-centre team. Agents can look up a learner by mobile number to see their profile status and points passbook — no editing, no admin access.</p>
            </div>

            {lastCreated && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="callcenter-created-creds">
                    <div className="text-sm font-semibold text-emerald-800">Share these credentials with the agent (shown once):</div>
                    <div className="mt-2 font-mono text-sm text-slate-800">Email: {lastCreated.email}<br />Password: {lastCreated.password}</div>
                    <button onClick={copyCreds} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-900" data-testid="callcenter-copy-creds">
                        {copied ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
                    </button>
                </div>
            )}

            <form onSubmit={create} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="callcenter-create-form">
                <h3 className="font-display font-bold text-slate-900 flex items-center gap-2 mb-4"><UserPlus className="h-5 w-5 text-brand-yellow" /> New agent login</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <label className="text-xs font-bold uppercase tracking-wide text-slate-500 flex flex-col gap-1">Full name
                        <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal normal-case text-slate-800" data-testid="callcenter-name-input" />
                    </label>
                    <label className="text-xs font-bold uppercase tracking-wide text-slate-500 flex flex-col gap-1">Email
                        <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal normal-case text-slate-800" data-testid="callcenter-email-input" />
                    </label>
                    <label className="text-xs font-bold uppercase tracking-wide text-slate-500 flex flex-col gap-1">Password
                        <input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} placeholder="min 6 characters" className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono font-normal normal-case text-slate-800" data-testid="callcenter-password-input" />
                    </label>
                </div>
                <button type="submit" disabled={busy} className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-yellow text-brand-black font-semibold disabled:opacity-50" data-testid="callcenter-create-btn">
                    <UserPlus className="h-4 w-4" /> {busy ? 'Creating…' : 'Create login'}
                </button>
            </form>

            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100 font-display font-bold text-slate-900">Agents ({agents.length})</div>
                {loading ? <div className="p-5 text-slate-500 text-sm">Loading…</div> : (
                    agents.length === 0 ? <div className="p-5 text-slate-400 text-sm" data-testid="callcenter-empty">No call-centre logins yet.</div> : (
                        <table className="min-w-full text-sm">
                            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                                <tr><th className="text-left px-5 py-2">Name</th><th className="text-left px-5 py-2">Email</th><th className="text-left px-5 py-2">Status</th><th className="text-left px-5 py-2">Last login</th><th className="text-right px-5 py-2">Actions</th></tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {agents.map((a) => (
                                    <tr key={a.id} data-testid={`callcenter-agent-${a.id}`}>
                                        <td className="px-5 py-3 font-semibold text-slate-800">{a.full_name || '—'}</td>
                                        <td className="px-5 py-3 font-mono text-xs text-slate-600">{a.email}</td>
                                        <td className="px-5 py-3">
                                            {a.is_active ? <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold">● Active</span>
                                                : <span className="inline-flex items-center gap-1 text-rose-500 text-xs font-semibold">● Inactive</span>}
                                        </td>
                                        <td className="px-5 py-3 text-xs text-slate-500">{a.last_login_at ? new Date(a.last_login_at).toLocaleString() : 'Never'}</td>
                                        <td className="px-5 py-3 text-right whitespace-nowrap">
                                            <button onClick={() => setResetFor(a)} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-brand-blue mr-3" data-testid={`callcenter-reset-${a.id}`}><KeyRound className="h-3.5 w-3.5" /> Reset password</button>
                                            <button onClick={() => toggleActive(a)} className={`inline-flex items-center gap-1 text-xs font-semibold ${a.is_active ? 'text-rose-600 hover:text-rose-700' : 'text-emerald-600 hover:text-emerald-700'}`} data-testid={`callcenter-toggle-${a.id}`}>
                                                {a.is_active ? <><PowerOff className="h-3.5 w-3.5" /> Deactivate</> : <><Power className="h-3.5 w-3.5" /> Activate</>}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )
                )}
            </div>

            {resetFor && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setResetFor(null)} data-testid="callcenter-reset-modal">
                    <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2"><KeyRound className="h-5 w-5 text-brand-blue" /> Reset password</h3>
                        <p className="text-sm text-slate-600 mt-1">for <b>{resetFor.email}</b></p>
                        <input type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password (min 6 chars)" className="mt-4 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono" data-testid="callcenter-reset-input" />
                        <div className="flex justify-end gap-2 mt-4">
                            <button onClick={() => { setResetFor(null); setNewPw(''); }} className="px-4 py-2 text-sm font-semibold text-slate-600">Cancel</button>
                            <button onClick={submitReset} className="px-4 py-2 rounded-xl bg-brand-blue text-white text-sm font-semibold" data-testid="callcenter-reset-submit">Set password</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
